'use client'

import { AppointmentButton } from '@/components/appointment-button'
import { Progress } from '@/components/progress'
import { StepCircle } from '@/components/step-circle'
import { useRenderEverySecond, useSocketConnection, useWebsocketHandlers } from '@/hooks'
import { useConfig } from '@/providers/ConfigContext'
import { IAppointment, IServiceAppointments } from '@/service/types'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import GroupIcon from '@mui/icons-material/Group'
import TodayIcon from '@mui/icons-material/Today'
import {
  Alert,
  Box,
  Container,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { FormikHelpers } from 'formik'
import { useEffect, useState } from 'react'
import { Socket } from 'socket.io-client'
import { BookingForm, IBookingFormDto } from './booking-form'
import FreeAppointments from './free-appointments'
import { HelpContent } from './help-content'
import SelectServicesStep from './select-services-step'
import { ServiceAppointments } from './service-appointments'

export interface IEventContainerProps {
  eventId: string
  socket: Socket
}

export const EventDetailContent = (props: IEventContainerProps) => {
  const { eventId, socket } = props
  const isConnected = useSocketConnection(socket)
  const { services, clientId, eventData } = useWebsocketHandlers(socket)
  const [showProgress, setShowProgress] = useState(true)
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [showBookedAppointments, setShowBookedAppointments] = useState(false)

  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('md'))
  const config = useConfig()

  useRenderEverySecond()

  useEffect(() => {
    if (socket.connected) {
      // for hot reload
      if (clientId) {
        socket.emit('leaveEventRoom', {
          eventId,
          clientId,
        })
      }

      socket.emit('joinEventRoom', {
        eventId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, socket.connected])

  useEffect(() => {
    const isConnectedFully = !!socket && isConnected && !!clientId
    if (isConnectedFully) {
      setTimeout(() => {
        setShowProgress(false)
      }, 500)
    }
  }, [socket, isConnected, clientId])

  const handleTimeslotClick = (
    appointment: string,
    service: string,
    startDate: string,
    endDate: string,
    action: 'bookAppointment' | 'releaseAppointment',
  ) => {
    if (clientId) {
      socket.emit(action, {
        eventId,
        appointment,
        startDate,
        endDate,
        service,
      })
    }
  }

  const handleSubmit = (
    values: IBookingFormDto,
    { setSubmitting }: FormikHelpers<IBookingFormDto>,
  ) => {
    if (clientId) {
      socket.emit('finishBooking', {
        ...values,
        eventId,
        company: eventData?.company,
      })

      // The user should on the success page already...
      // If not, then it's an error and we must enable booking button
      setTimeout(() => {
        setSubmitting(false)
      }, 4000)
    }
  }

  const handleSelectService = (selected: IServiceAppointments) => {
    const { service, hours } = selected
    const isSelected = selectedServices.includes(service)
    if (isSelected) {
      let myAppointment = ''
      hours.forEach(({ appointments }) => {
        appointments.forEach((appointment) => {
          if (appointment.isLocked && appointment.visitor === clientId) {
            myAppointment = appointment.id
          }
        })
      })

      if (myAppointment) {
        const message = `Már foglaltál egy időpontot erre a szolgáltatásra, biztos hogy törölni szeretnéd?`

        const confirmed = window.confirm(message)

        // Remove selected service and booked appointment
        if (confirmed) {
          setSelectedServices(selectedServices.filter((s) => s !== service))
          socket.emit('releaseAppointment', {
            eventId,
            appointment: myAppointment,
            service,
          })
        }
      } else {
        // Remove selected service withouth booked appointment
        setSelectedServices(selectedServices.filter((s) => s !== service))
      }
    } else {
      // Add selected service
      setSelectedServices([...selectedServices, service])
    }
  }

  if (showProgress) {
    return (
      <Progress
        stages={[
          { isLoaded: !!socket, text: 'Oldal betöltése...' },
          { isLoaded: isConnected, text: 'Kapcsolódás a szerverhez...' },
          { isLoaded: !!clientId, text: 'Időpontok betöltése...' },
        ]}
      />
    )
  }

  return (
    <SiteContainer>
      <HeaderContainer sx={{ textAlign: 'left' }}>
        <Typography variant="h1" sx={{ paddingBottom: 1, paddingTop: 5 }}>
          {eventData?.title}
        </Typography>

        <HelpContent />

        <Typography variant="body2" mb={1.5}>
          <TodayIcon sx={{ mr: 0.5, verticalAlign: 'middle', mt: -0.5 }} fontSize="medium" />
          <span style={{ fontWeight: 600 }}>Dátum: </span>
          {dayjs(eventData?.startDate).format('YYYY.MM.DD. HH:mm')} -{' '}
          {dayjs(eventData?.endDate).format('HH:mm')}
        </Typography>

        <Typography variant="body2" mb={1.5}>
          <TodayIcon sx={{ mr: 0.5, verticalAlign: 'middle', mt: -0.5 }} fontSize="medium" />
          <span style={{ fontWeight: 600 }}>Foglalási időszak: </span>
          {dayjs(eventData?.startOfBookingPeriod).format('YYYY.MM.DD. HH:mm')} -{' '}
          {dayjs(eventData?.endOfBookingPeriod).format('YYYY.MM.DD. HH:mm')}
        </Typography>

        <Box display="flex" flexDirection={matches ? 'row' : 'column'}>
          <Typography variant="body2" mb={1.5} mr={2}>
            <GroupIcon sx={{ mr: 0.5, verticalAlign: 'middle', mt: -0.5 }} fontSize="medium" />
            <span style={{ fontWeight: 600 }}>Szabad időpontok: </span>
          </Typography>
          <FreeAppointments services={services} />
        </Box>

        <Typography sx={{ wordWrap: 'break-word' }} variant="body2" mb={4}>
          <ContentPasteIcon sx={{ mr: 0.5, verticalAlign: 'middle', mt: -0.5 }} fontSize="medium" />
          <span style={{ fontWeight: 600 }}>Leírás: </span>
          {eventData?.description}
        </Typography>
      </HeaderContainer>

      {/* STEP 1 */}
      <SelectServicesStep
        services={services}
        selectedServices={selectedServices}
        handleSelectService={handleSelectService}
      />

      {/* STEP 2 */}
      <ServicesPaper elevation={0}>
        <StepCircle number="2" />
        <Box
          display="flex"
          justifyContent="space-between"
          marginTop={matches ? 0 : 1}
          marginLeft={matches ? 0 : 1}
          sx={{ flexWrap: 'wrap' }}
        >
          <Box pb={1}>
            <Typography variant="h6">Válaszd ki a szűrés(ek) időpontját.</Typography>
          </Box>
          <Box pl={1}>
            <FormControlLabel
              control={
                <Switch
                  defaultChecked={showBookedAppointments}
                  size="small"
                  onChange={() => setShowBookedAppointments(!showBookedAppointments)}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 500, pl: 1 }}>
                  Mutasd a foglalt időpontokat is.
                </Typography>
              }
            />
          </Box>
        </Box>
        <Alert severity="info" sx={{ marginX: 1, lineHeight: 1.7 }}>
          FONTOS!
          <br />A regisztrációhoz{' '}
          {Number.isInteger(config.reserveAppointmentLockInSeconds / 60)
            ? config.reserveAppointmentLockInSeconds / 60
            : (config.reserveAppointmentLockInSeconds / 60).toFixed(2)}{' '}
          perc áll a rendelkezésére. Ha túllépi az időt, újra kell kezdeni a folyamatot.
          <br />
          Köszönettel V&H Medical csapata
        </Alert>

        {selectedServices.length === 0 && (
          <Paper
            elevation={1}
            sx={{
              textAlign: 'center',
              padding: 1,
              marginX: 1,
              marginY: 1,
            }}
          >
            <Typography variant="body1">Először válassz szűrést az első lépésben!</Typography>
          </Paper>
        )}

        {services
          .filter(({ service }) => selectedServices.includes(service))
          .map(({ service, hours }) => (
            <ServiceAppointments
              key={service}
              service={service}
              hours={hours}
              clientId={clientId}
              showBookedAppointments={showBookedAppointments}
              renderAppointment={(appointment: IAppointment) => (
                <AppointmentButton
                  key={appointment.id}
                  clientId={clientId}
                  service={service}
                  handleTimeslotClick={handleTimeslotClick}
                  appointment={appointment}
                />
              )}
            />
          ))}
      </ServicesPaper>

      {/* STEP 3 */}
      <Paper elevation={0} sx={{ padding: 3, marginY: 2, position: 'relative' }}>
        <StepCircle number="3" />

        <Typography
          variant="h6"
          marginTop={matches ? 0 : 1}
          // marginLeft={matches ? 0 : 1}
        >
          Adataid megadásával és a &quot;Foglalás beküldése&quot; gomb megnyomásával véglegesítheted
          foglalásod.
        </Typography>

        <BookingForm handleSubmit={handleSubmit} />
      </Paper>
    </SiteContainer>
  )
}

const ServicesPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(13),
  marginBottom: theme.spacing(13),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const SiteContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
}))

const HeaderContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(3),
  },
}))
